import React, { useEffect, useState } from 'react';
import { Table, Typography, Tag, Space, Button, Select, Tooltip, Empty, Card, Descriptions, Divider, Modal, Row, Col } from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CreditCardOutlined,
  BankOutlined,
  WalletOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { useGetCashRegisterHistoryQuery } from '../../services/cashRegisterApi';
import { CashRegisterDto } from '../../types/cashRegister';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';

const { Text } = Typography;
const { Option } = Select;

interface CashRegisterHistoryTabProps {
  restaurantId?: string; // Not used directly, restaurant context comes from API
}

const CashRegisterHistoryTab: React.FC<CashRegisterHistoryTabProps> = () => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const {
    startOfDay,
    isLoading: isTimeZoneLoading,
    formatInTimeZone,
    getLocalDate,
  } = useRestaurantTimeZone();

  // Default to last 7 days in the restaurant's timezone. Mirrors the pattern
  // in CashRegisterHistory.tsx (PR #40 review fix): we defer the initial
  // range until the timezone setting resolves, then seed it once. Manual
  // user changes are preserved afterwards.
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  useEffect(() => {
    if (isTimeZoneLoading || dateRange !== null) return;
    const today = startOfDay();
    setDateRange([today.subtract(7, 'day'), today.endOf('day')]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTimeZoneLoading]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedRegister, setSelectedRegister] = useState<CashRegisterDto | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data: history, isLoading, refetch } = useGetCashRegisterHistoryQuery({
    query: {
      fromDate: dateRange?.[0]?.startOf('day').toISOString(),
      toDate: dateRange?.[1]?.endOf('day').toISOString(),
      status: statusFilter === 'all' ? undefined : statusFilter,
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'green';
      case 'CLOSED':
        return 'blue';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'OPEN':
        return 'Abierta';
      case 'CLOSED':
        return 'Cerrada';
      default:
        return status;
    }
  };

  const getVarianceColor = (variance: number) => {
    if (Math.abs(variance) < 1) return 'var(--color-success)';
    if (Math.abs(variance) < 10) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const formatDuration = (openTime: string, closeTime?: string) => {
    if (!closeTime) return 'En curso';

    const start = dayjs(openTime);
    const end = dayjs(closeTime);
    const duration = end.diff(start, 'minute');

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const handleViewDetails = (record: CashRegisterDto) => {
    setSelectedRegister(record);
    setShowDetailModal(true);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedRegister(null);
  };

  const handleExport = () => {
    if (!history || history.length === 0) return;

    const headers = [
      'Usuario', 'Apertura', 'Cierre', 'Duracion', 'Monto Inicial',
      'Ventas Efectivo', 'Ventas Tarjeta', 'Ventas Transferencia', 'Ventas Otros',
      'Total Ventas', 'Propinas', 'Ingresos', 'Gastos', 'Retiros', 'Pagos',
      'Monto Esperado', 'Monto Final', 'Diferencia', 'Estado'
    ];

    const rows = history.map(record => [
      record.userName,
      formatInTimeZone(record.openTime, 'DD/MM/YYYY HH:mm'),
      record.closeTime ? formatInTimeZone(record.closeTime, 'DD/MM/YYYY HH:mm') : '-',
      formatDuration(record.openTime, record.closeTime),
      (record.initialAmount ?? 0).toFixed(2),
      (record.summary?.totalSales ?? 0).toFixed(2),
      (record.summary?.totalCardSales ?? 0).toFixed(2),
      (record.summary?.totalTransferSales ?? 0).toFixed(2),
      (record.summary?.totalOtherSales ?? 0).toFixed(2),
      (record.summary?.totalAllSales ?? 0).toFixed(2),
      (record.summary?.totalTips ?? 0).toFixed(2),
      (record.summary?.totalCorrections ?? 0).toFixed(2),
      (record.summary?.totalExpenses ?? 0).toFixed(2),
      (record.summary?.totalDrops ?? 0).toFixed(2),
      (record.summary?.totalPayouts ?? 0).toFixed(2),
      (record.expectedAmount ?? 0).toFixed(2),
      record.finalAmount?.toFixed(2) ?? '-',
      (record.variance ?? 0).toFixed(2),
      record.status === 'OPEN' ? 'Abierta' : 'Cerrada'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `historial-cajas-${getLocalDate('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderMobileCard = (record: CashRegisterDto) => (
    <Card
      key={record.id}
      style={{ marginBottom: 12 }}
      size="small"
      actions={[
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          size="small"
        >
          Ver Detalles
        </Button>
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <UserOutlined />
            <Text strong>{record.userName}</Text>
          </Space>
          <Tag color={getStatusColor(record.status)} style={{ fontSize: '10px' }}>
            {getStatusText(record.status)}
          </Tag>
        </div>

        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Apertura">
            {formatInTimeZone(record.openTime, 'DD/MM/YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Cierre">
            {record.closeTime ? formatInTimeZone(record.closeTime, 'DD/MM/YYYY HH:mm') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Duracion">
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {formatDuration(record.openTime, record.closeTime)}
          </Descriptions.Item>
          <Descriptions.Item label="Ventas">
            <Text strong style={{ color: 'var(--color-success-text)' }}>
              {currencySymbol} {(record.summary?.totalAllSales ?? 0).toFixed(2)}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Diferencia">
            <Text strong style={{ color: getVarianceColor(record.variance ?? 0) }}>
              {(record.variance ?? 0) > 0 ? '+' : ''}{currencySymbol} {(record.variance ?? 0).toFixed(2)}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Space>
    </Card>
  );

  const columns = [
    {
      title: 'Usuario',
      dataIndex: 'userName',
      key: 'userName',
      width: 120,
      render: (text: string) => <Text strong style={{ fontSize: '13px' }}>{text}</Text>,
    },
    {
      title: 'Apertura',
      dataIndex: 'openTime',
      key: 'openTime',
      width: 100,
      render: (time: string) => (
        <Text style={{ fontSize: '12px' }}>{formatInTimeZone(time, 'DD/MM HH:mm')}</Text>
      ),
    },
    {
      title: 'Cierre',
      dataIndex: 'closeTime',
      key: 'closeTime',
      width: 100,
      render: (time: string | null) => (
        <Text style={{ fontSize: '12px' }}>{time ? formatInTimeZone(time, 'DD/MM HH:mm') : '-'}</Text>
      ),
    },
    {
      title: 'Duracion',
      key: 'duration',
      width: 80,
      render: (record: CashRegisterDto) => (
        <Text style={{ fontSize: '12px' }}>
          <ClockCircleOutlined style={{ marginRight: 4 }} />
          {formatDuration(record.openTime, record.closeTime)}
        </Text>
      ),
    },
    {
      title: 'Ventas',
      key: 'sales',
      width: 100,
      align: 'right' as const,
      render: (record: CashRegisterDto) => (
        <Text strong style={{ color: 'var(--color-success-text)', fontSize: '13px' }}>
          {currencySymbol} {(record.summary?.totalAllSales ?? 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Diferencia',
      dataIndex: 'variance',
      key: 'variance',
      width: 100,
      align: 'right' as const,
      render: (variance: number | null) => (
        <Text strong style={{ color: getVarianceColor(variance ?? 0), fontSize: '13px' }}>
          {(variance ?? 0) > 0 ? '+' : ''}{currencySymbol} {(variance ?? 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} style={{ fontSize: '11px' }}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 80,
      align: 'center' as const,
      render: (record: CashRegisterDto) => (
        <Tooltip title="Ver detalles">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <div style={{ padding: isMobile ? 0 : '16px 0' }}>
        {/* Filters */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={10} md={8}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Periodo:
            </Text>
            <MobileDateRangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              size={isMobile ? 'middle' : 'middle'}
            />
          </Col>

          <Col xs={12} sm={6} md={4}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              Estado:
            </Text>
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              size={isMobile ? 'middle' : 'middle'}
              style={{ width: '100%' }}
            >
              <Option value="all">Todos</Option>
              <Option value="OPEN">Abiertas</Option>
              <Option value="CLOSED">Cerradas</Option>
            </Select>
          </Col>

          <Col xs={12} sm={8} md={4} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetch()}
              size={isMobile ? 'middle' : 'middle'}
            >
              {!isMobile && 'Actualizar'}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              size={isMobile ? 'middle' : 'middle'}
            >
              {isMobile ? '' : 'Exportar'}
            </Button>
          </Col>
        </Row>

        {/* Period totals as pulse chips — same language as Cajas Abiertas/Mi Caja.
            Color stays reserved for retiros (out) and diferencia (health). */}
        {history && history.length > 0 && (() => {
          const sumOf = (pick: (item: CashRegisterDto) => number) =>
            history.reduce((sum, item) => sum + pick(item), 0);
          const totalVariance = sumOf(item => item.variance ?? 0);
          const totalDrops = sumOf(item => item.summary?.totalDrops || 0);
          const money = (n: number) => `${currencySymbol}${n.toFixed(2)}`;
          return (
            <div className="cash-bar" style={{ marginBottom: 16 }}>
              <span className="cash-bar__chip"><b>{history.length}</b>&nbsp;{history.length === 1 ? 'caja' : 'cajas'}</span>
              <span className="cash-bar__chip">ventas&nbsp;<b>{money(sumOf(i => i.summary?.totalAllSales || 0))}</b></span>
              <span className="cash-bar__chip">efectivo&nbsp;<b>{money(sumOf(i => i.summary?.totalSales || 0))}</b></span>
              <span className="cash-bar__chip">tarjeta&nbsp;<b>{money(sumOf(i => i.summary?.totalCardSales || 0))}</b></span>
              <span className="cash-bar__chip">transfer.&nbsp;<b>{money(sumOf(i => i.summary?.totalTransferSales || 0))}</b></span>
              {totalDrops > 0 && (
                <span className="cash-bar__chip">retiros&nbsp;<b style={{ color: 'var(--color-error-text)' }}>{money(totalDrops)}</b></span>
              )}
              <span className="cash-bar__chip">
                diferencia&nbsp;<b style={{ color: getVarianceColor(totalVariance) }}>{money(totalVariance)}</b>
              </span>
            </div>
          );
        })()}

        {/* Table or Cards */}
        {isMobile ? (
          isLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
          ) : history && history.length > 0 ? (
            <div style={{ maxHeight: 'calc(100vh - 350px)', overflowY: 'auto' }}>
              {history.map(record => renderMobileCard(record))}
            </div>
          ) : (
            <Empty
              description="No hay historial de cajas registradoras"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 40 }}
            />
          )
        ) : (
          <Table
            columns={columns}
            dataSource={history || []}
            loading={isLoading}
            rowKey="id"
            size="middle"
            scroll={{ x: 800 }}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
            }}
            locale={{
              emptyText: (
                <Empty
                  description="No hay historial de cajas registradoras"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        )}
      </div>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <DollarOutlined />
            <span>Detalle de Caja Registradora</span>
          </Space>
        }
        open={showDetailModal}
        onCancel={handleCloseDetailModal}
        footer={[
          <Button key="close" onClick={handleCloseDetailModal}>
            Cerrar
          </Button>
        ]}
        width={isMobile ? '98%' : 600}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        {selectedRegister && !selectedRegister.summary && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Empty
              description="No se pudo cargar la informacion de la caja"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )}
        {selectedRegister && selectedRegister.summary && (
          <div style={{ padding: isMobile ? '8px 0' : '16px 0' }}>
            {/* Header Info */}
            <div style={{
              padding: '12px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: 8,
              marginBottom: 16,
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Space>
                    <UserOutlined />
                    <Text strong style={{ fontSize: 16 }}>{selectedRegister.userName}</Text>
                  </Space>
                  <Tag color={getStatusColor(selectedRegister.status)}>
                    {getStatusText(selectedRegister.status)}
                  </Tag>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                  <span>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    Apertura: {formatInTimeZone(selectedRegister.openTime, 'DD/MM/YYYY HH:mm')}
                  </span>
                  {selectedRegister.closeTime && (
                    <span>
                      Cierre: {formatInTimeZone(selectedRegister.closeTime, 'DD/MM/YYYY HH:mm')}
                    </span>
                  )}
                  <span>
                    Duracion: {formatDuration(selectedRegister.openTime, selectedRegister.closeTime)}
                  </span>
                </div>
              </Space>
            </div>

            {/* Cash Information */}
            <Divider orientation="left" style={{ fontSize: 14 }}>
              <DollarOutlined /> Efectivo
            </Divider>
            <Descriptions bordered size="small" column={isMobile ? 1 : 2}>
              <Descriptions.Item label="Monto Inicial">
                {currencySymbol} {(selectedRegister.initialAmount ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Ventas en Efectivo">
                <Text style={{ color: 'var(--color-success-text)' }}>
                  {currencySymbol} {(selectedRegister.summary?.totalSales ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Propinas">
                {currencySymbol} {(selectedRegister.summary?.totalTips ?? 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Ingresos">
                <Text style={{ color: 'var(--color-success-text)' }}>
                  +{currencySymbol} {(selectedRegister.summary?.totalCorrections ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Gastos">
                <Text style={{ color: 'var(--color-error-text)' }}>
                  -{currencySymbol} {(selectedRegister.summary?.totalExpenses ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Retiros">
                <Text style={{ color: 'var(--color-error-text)' }}>
                  -{currencySymbol} {(selectedRegister.summary?.totalDrops ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Pagos">
                <Text style={{ color: 'var(--color-error-text)' }}>
                  -{currencySymbol} {(selectedRegister.summary?.totalPayouts ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Monto Esperado">
                <Text strong>
                  {currencySymbol} {(selectedRegister.expectedAmount ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              {selectedRegister.finalAmount != null && (
                <Descriptions.Item label="Monto Final">
                  <Text strong>
                    {currencySymbol} {(selectedRegister.finalAmount ?? 0).toFixed(2)}
                  </Text>
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Diferencia" span={isMobile ? 1 : 2}>
                <Text strong style={{
                  color: getVarianceColor(selectedRegister.variance ?? 0),
                  fontSize: 16
                }}>
                  {(selectedRegister.variance ?? 0) > 0 ? '+' : ''}
                  {currencySymbol} {(selectedRegister.variance ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            {/* Sales by Payment Method */}
            <Divider orientation="left" style={{ fontSize: 14, marginTop: 24 }}>
              <WalletOutlined /> Ventas por Metodo de Pago
            </Divider>
            <Descriptions bordered size="small" column={isMobile ? 1 : 2}>
              <Descriptions.Item label={<><DollarOutlined /> Efectivo</>}>
                <Text style={{ color: 'var(--color-success-text)' }}>
                  {currencySymbol} {(selectedRegister.summary?.totalSales ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={<><CreditCardOutlined /> Tarjeta</>}>
                <Text style={{ color: 'var(--color-info-text)' }}>
                  {currencySymbol} {(selectedRegister.summary?.totalCardSales ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={<><BankOutlined /> Transferencia</>}>
                <Text style={{ color: 'var(--color-reserved-text)' }}>
                  {currencySymbol} {(selectedRegister.summary?.totalTransferSales ?? 0).toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Otros">
                {currencySymbol} {(selectedRegister.summary?.totalOtherSales ?? 0).toFixed(2)}
              </Descriptions.Item>
            </Descriptions>

            {/* Totals */}
            <div style={{
              marginTop: 16,
              padding: '12px',
              backgroundColor: 'var(--bg-success-light)',
              borderRadius: 8,
              border: '1px solid var(--border-success)',
            }}>
              <Space wrap size={24} style={{ justifyContent: 'space-between', width: '100%' }}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Total Ventas (Todos los metodos)</Text>
                  <br />
                  <Text strong style={{ fontSize: 18, color: 'var(--color-success-text)' }}>
                    {currencySymbol} {(selectedRegister.summary?.totalAllSales ?? 0).toFixed(2)}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Ingresos Totales (+ Propinas)</Text>
                  <br />
                  <Text strong style={{ fontSize: 18, color: 'var(--color-info-text)' }}>
                    {currencySymbol} {(selectedRegister.summary?.totalRevenue ?? 0).toFixed(2)}
                  </Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Total Transacciones</Text>
                  <br />
                  <Text strong style={{ fontSize: 18 }}>
                    {selectedRegister.summary?.totalTransactions ?? 0}
                  </Text>
                </div>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default CashRegisterHistoryTab;
